import { cx } from '@linaria/core';
import { useRef, useId, useEffect, useCallback, useState } from 'react';
import { pm2Boundaries } from '@pm2/design-tokens';
import { ElementToolbar } from '@pm2/element-toolbar';
import { breakpoints } from '@ui/css';
import { componentFrameworkCloseButton } from '@ui/kit';
import { CrossIcon } from '@ui/kit/icons';
import { usePlatformIOS } from '@utils/common';
import { ComponentFrameworkTitle, ComponentFrameworkSaveIndicator, useMutateEntry } from '@utils/component-framework';
import { useFullscreenState, useSwipe, useElementSize, HideFor, ShowFor, useChangeEffect, useMatchMedia } from '@utils/react';
import { NavigationButton } from '../root.content/atom.navigation-button.js';
import { useIArkNavigation } from '../root.content/hook.navigation.js';
import { PrintLayout } from '../root.content/organism.print/_print.root.js';
import '@i-ark/constants';
import '@i-ark/models';
import '@tanstack/react-query';
import { selectPrintOrientation } from '../state/selectors/print.get-orientation.js';
import { iArkStore } from '../state/store.js';
import { sheetCls } from '../root.content/organism.sheet/_sheet.style.js';
import '../root.content/organism.print/_print.root.style.js';
import { IArkTools } from '../root.content/organism.tools/_tools.js';
import { useIArkFullscreenActions } from '../root.content/organism.tools/hooks/useFullscreenActions.js';
import { actionsSize, tokens, fullscreenCloseButton, header, headingWrapper, contextMenu, headerToolsWrapper, saveIndicatorPlaceholder, saveIndicator, bullets, actionsWrapper, actions, elementToolbarPlaceholder, fullscreenNavigationButton, saveIndicatorWrapper, mobileLightboxLayoutBreakpoint, contentWrapper, bottomContext, tools, wrapper, overlayContainer, root } from './_component.style.js';
import { Bullets } from './atom.bullets.js';
import { IArkContextMenu } from './integration.context-menu.js';
import { IArkRetryButton } from './modal.retry.js';
import { EmbedWrapper } from './variant.embed.js';
import { StandaloneWrapper } from './variant.standalone.js';
import { jsxs, jsx } from 'react/jsx-runtime';

function IArkComponent(props) {
  const internalRef = useRef(null);
  const ref = props.ref ?? internalRef;
  const actionsRef = useRef(null);
  const sectionRef = useRef(null);
  const internalContentRef = useRef(null);
  const contentRef = props.contentRef ?? internalContentRef;
  const pageWrapperRef = useRef(null);
  const id = useId();
  const overlayContainerId = `overlay-container-${id}`;
  const rootSelector = `[data-i-ark-id='${props.node.component.envelope.ID}']`;
  const entryKey = iArkStore.useSelector(state => state.entryKey);
  const navigation = useIArkNavigation();
  useSaveEntryAfterReset();
  const {
    fullscreenEnabled
  } = useFullscreenState();
  const isFullscreen = fullscreenEnabled || props.context === "lightbox";
  const shouldMoveSheetUp = useFocusWithinViewportWithVirtualKeyboard(isFullscreen);
  const orientation = iArkStore.useSelector(selectPrintOrientation);
  const {
    handleExitFullscreenClick
  } = useIArkFullscreenActions({
    componentProps: props,
    overlayId: overlayContainerId,
    rootSelector
  });
  useEffect(() => {
    if (isFullscreen) {
      props.onMountedInFullscreen?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullscreen]);
  useEffect(() => {
    if (!props.onCollapse) {
      return;
    }
    ref.current?.focus({
      preventScroll: true
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  pm2Boundaries.useContentEngineBoundaryProps(contentRef, {
    observerTarget: "self"
  });
  const onSwipe = useCallback(dir => {
    switch (dir) {
      case "left-to-right":
        {
          if (navigation.canGoBackwards) {
            navigation.handleGoBackwards();
          }
          break;
        }
      case "right-to-left":
        {
          if (navigation.canGoForwards) {
            navigation.handleGoForwards();
          }
          break;
        }
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [navigation.canGoBackwards, navigation.canGoForwards, navigation.handleGoBackwards, navigation.handleGoForwards]);
  useSwipe(contentRef, onSwipe, {
    disabled: isFullscreen,
    touchOnly: true
  });
  useElementSize(actionsRef, {
    axis: "vertical",
    applyTo: ref,
    varName: actionsSize.propName
  });
  const firstPage = props.node.component.data.pages[0];
  return /*#__PURE__*/jsxs("div", {
    ref: ref,
    "data-i-ark-id": props.node.component.envelope.ID,
    className: cx(props.className, root),
    tabIndex: -1,
    children: [/*#__PURE__*/jsxs("section", {
      id: id,
      ref: sectionRef,
      className: cx(wrapper),
      "data-context": fullscreenEnabled ? "fullscreen" : props.context,
      "data-fullscreen": isFullscreen,
      "data-orientation": orientation,
      style: firstPage ? tokens.firstPageAspectRatio.__override(firstPage.aspectRatio) : undefined,
      children: [isFullscreen && props.context !== "lightbox" && /*#__PURE__*/jsx("button", {
        className: cx(componentFrameworkCloseButton, fullscreenCloseButton),
        "data-theme": "component-framework",
        "aria-label": "Luk fuldsk\xE6rmsvisning",
        onClick: handleExitFullscreenClick,
        children: /*#__PURE__*/jsx(CrossIcon, {
          height: "13"
        })
      }), /*#__PURE__*/jsxs("header", {
        className: header,
        children: [/*#__PURE__*/jsxs("div", {
          className: headingWrapper,
          children: [/*#__PURE__*/jsx(ComponentFrameworkTitle, {
            title: props.node.component.envelope.Title,
            onCollapse: props.onCollapse,
            richtextDescription: props.context === "embedded" ? props.node?.meta.richTextDescription : undefined
          }), props.node && props.context !== "standalone" && !isFullscreen && /*#__PURE__*/jsx(HideFor, {
            breakpoint: breakpoints.mobile.andDown(),
            className: contextMenu,
            children: /*#__PURE__*/jsx(ElementToolbar, {
              node: props.node
            })
          })]
        }), /*#__PURE__*/jsxs("div", {
          className: headerToolsWrapper,
          children: [/*#__PURE__*/jsx("div", {
            className: cx(saveIndicatorPlaceholder)
          }), isFullscreen && /*#__PURE__*/jsx("span", {
            className: saveIndicator,
            children: /*#__PURE__*/jsx(ComponentFrameworkSaveIndicator, {
              entryKey: entryKey
            })
          }), /*#__PURE__*/jsx(Bullets, {
            className: cx(bullets, "above"),
            component: props.node.component
          }), /*#__PURE__*/jsxs("div", {
            ref: actionsRef,
            className: actionsWrapper,
            children: [/*#__PURE__*/jsx(IArkContextMenu, {
              className: actions,
              layout: "fab",
              targetRef: ref,
              component: props.node.component,
              context: props.context,
              onShareWithTeacher: props.onShareWithTeacher
            }), /*#__PURE__*/jsx(ShowFor, {
              breakpoint: breakpoints.mobile.andDown(),
              children: /*#__PURE__*/jsx("div", {
                className: elementToolbarPlaceholder,
                children: props.node && props.context !== "standalone" && !isFullscreen && /*#__PURE__*/jsx(ElementToolbar, {
                  node: props.node
                })
              })
            })]
          })]
        })]
      }), isFullscreen && /*#__PURE__*/jsx("span", {
        className: cx(fullscreenNavigationButton, "left"),
        children: /*#__PURE__*/jsx(NavigationButton, {
          direction: "backwards",
          component: props.node.component
        })
      }), /*#__PURE__*/jsxs("div", {
        className: cx(contentWrapper, shouldMoveSheetUp && "movedUp"),
        ref: contentRef,
        children: [!isFullscreen && /*#__PURE__*/jsx("div", {
          className: saveIndicatorWrapper,
          children: /*#__PURE__*/jsx("span", {
            className: saveIndicator,
            children: /*#__PURE__*/jsx(ComponentFrameworkSaveIndicator, {
              entryKey: entryKey
            })
          })
        }), props.context === "embedded" ? /*#__PURE__*/jsx(EmbedWrapper, {
          rootRef: ref,
          ...props,
          rootSelector: rootSelector,
          overlayId: overlayContainerId,
          contentWrapperRef: pageWrapperRef
        }) : /*#__PURE__*/jsx(StandaloneWrapper, {
          rootRef: ref,
          ...props,
          rootSelector: rootSelector,
          overlayId: overlayContainerId,
          contentWrapperRef: pageWrapperRef
        }), isFullscreen && /*#__PURE__*/jsx(ShowFor, {
          breakpoint: mobileLightboxLayoutBreakpoint,
          children: /*#__PURE__*/jsx(Bullets, {
            className: bullets,
            component: props.node.component
          })
        })]
      }), isFullscreen && /*#__PURE__*/jsx("span", {
        className: cx(fullscreenNavigationButton, "right"),
        children: /*#__PURE__*/jsx(NavigationButton, {
          direction: "forwards",
          component: props.node.component,
          onClick: () => {
            pageWrapperRef.current?.focus({
              preventScroll: true
            });
          }
        })
      }), /*#__PURE__*/jsxs("div", {
        className: bottomContext,
        children: [/*#__PURE__*/jsx(HideFor, {
          breakpoint: breakpoints.mobile,
          children: /*#__PURE__*/jsx(IArkRetryButton, {
            context: "button"
          })
        }), /*#__PURE__*/jsx(IArkTools, {
          className: tools,
          componentProps: props,
          rootRef: ref,
          rootSelector: rootSelector,
          contentRef: sectionRef,
          overlayContainerId: overlayContainerId,
          rootId: id
        })]
      }), !isFullscreen && /*#__PURE__*/jsx(ShowFor, {
        breakpoint: breakpoints.mobile,
        children: /*#__PURE__*/jsx(Bullets, {
          className: bullets,
          component: props.node.component
        })
      })]
    }), /*#__PURE__*/jsx(PrintLayout, {
      component: props.node.component.data
    }), /*#__PURE__*/jsx("div", {
      className: overlayContainer,
      id: overlayContainerId
    })]
  });
}

/**
 * In case the device used to access the sheet uses a virtual keyboard, then we
 * may need to move the sheet upwards to ensure that inputs does not get hidden
 * below the virtual keyboard. This is at least the case for Android devices,
 * but not iOS devices.
 * This hook checks if the sheet should be moved up to
 * accomodate for the space taken by the virtual keyboard.
 */
function useFocusWithinViewportWithVirtualKeyboard(isFullscreen) {
  const [moveSheetUp, setMoveUp] = useState(false);
  const isTouchDevice = useMatchMedia(`(pointer: coarse)`);
  const isIOS = usePlatformIOS();
  useEffect(() => {
    if (!isTouchDevice || !isFullscreen || isIOS) {
      return;
    }
    const onViewportResize = () => {
      const hasEnabledVirtualKeyboard = (window.visualViewport?.height ?? window.innerHeight) < window.innerHeight;
      if (hasEnabledVirtualKeyboard) {
        const focusedElementRect = document.activeElement?.getBoundingClientRect();
        const sheet = document.querySelector(`.${sheetCls}`)?.getBoundingClientRect();
        if (!focusedElementRect || !sheet) {
          return false;
        }
        const yPos = (focusedElementRect.top - sheet.top) / sheet.height;
        setMoveUp(yPos > 0.5);
      } else {
        setMoveUp(false);
      }
    };
    window.visualViewport?.addEventListener("rezize", onViewportResize);
    window.addEventListener("resize", onViewportResize);
    window.addEventListener("orientationchange", onViewportResize);
    return () => {
      window.visualViewport?.removeEventListener("rezize", onViewportResize);
      window.removeEventListener("resize", onViewportResize);
      window.removeEventListener("orientationchange", onViewportResize);
    };
  }, [isFullscreen, isIOS, isTouchDevice]);
  return moveSheetUp;
}

/**
 * After the entry has been updated in redux, this hook ensures that the reset
 * will also be flushed to the backend through tanstack query.
 */
function useSaveEntryAfterReset() {
  const entryKey = iArkStore.useSelector(state => state.entryKey);
  const updateEntry = useMutateEntry(entryKey);
  useChangeEffect(() => {
    updateEntry(entry => entry);
  }, [entryKey]);
}

export { IArkComponent };
//# sourceMappingURL=_component.js.map
